import { useEffect, useRef } from "react";

import { useJitsu } from "@jitsu/react";
import { toast } from "react-hot-toast";

import {
  type RecordingDiagnostics,
  type TrackStatusInfo,
  useWebcamCapture,
  type UseWebcamCaptureReturn,
  useWebcamCaptureTelemetry,
} from "@/components/webcam-capture";
import type { CapturedMedia } from "@/components/webcam-capture/use-webcam-capture";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useAppDispatch, useAppSelector } from "@/data/hooks";
import { startRecordingEarly, stopRecordingEarly } from "@/features/socratic";
import {
  createAudioLevelWindowReporter,
  recordingBreadcrumb,
  type RecordingPhase,
} from "@/features/socratic/recording-diagnostics";
import {
  selectCameraRequiredDisabled,
  selectSocraticDialogueState,
} from "@/features/socratic/socratic-slice";
import { useSampleTracker } from "@/hooks/use-sample-tracker";
import type { LevelReading } from "@/lib/audio-level-windows";
import { getCurrentDate } from "@/utils/datetime";
import {
  getStoredCameraEnabled,
  getStoredPreferredDevices,
} from "@/utils/devices";

/**
 * Hook that wraps AV recording logic for the Socratic feature, including
 * auto-start/stop based on preparation/response timers and manual controls for
 * early start/stop, retake, and submit.
 */
export function useSocraticRecording({
  onRecordingStart,
  onCaptureReady,
  onLevelReading,
  blockAutoStart = false,
}: {
  /** Given the moment recording began, for the turn's integrity record. */
  onRecordingStart?: (recordingStartedAt: string) => void;
  onCaptureReady?: (
    media: CapturedMedia,
    durationSeconds: number,
    diagnostics: RecordingDiagnostics | undefined,
    /** The moment recording stopped. Required: it is always available here, and
     * a missing one leaves the turn's integrity record null. */
    recordingCompletedAt: string
  ) => void;
  /**
   * Every 250 ms audio reading, for the silent-audio nudge — which has to react
   * inside a preparation phase that can be as short as 10 s.
   */
  onLevelReading?: (reading: LevelReading) => void;
  /** Withholds auto-start until the preparation recorder (sharing the same
   * video track) has released it — avoids two MediaRecorders on one track. */
  blockAutoStart?: boolean;
}): {
  recorder: UseWebcamCaptureReturn;
  startRecordingEarly: () => void;
  stopRecordingEarly: () => void;
  submit: () => void;
} {
  const dispatch = useAppDispatch();
  const { track } = useJitsu();
  const dialogueTimeline = useAppSelector(selectSocraticDialogueState);
  const cameraRequiredDisabled = useAppSelector(selectCameraRequiredDisabled);

  // When camera is optional, restore the student's device-check preference.
  // When camera is required, always start with it on regardless of storage.
  const initialCameraEnabled = cameraRequiredDisabled
    ? (getStoredCameraEnabled() ?? true)
    : true;

  const dialogueId = dialogueTimeline.dialogueId;

  // Latest camera state
  const cameraEnabledRef = useRef(false);

  const phaseRef = useRef<RecordingPhase>("preparation");
  phaseRef.current =
    dialogueTimeline.preparationEndDate.passed === true
      ? "response"
      : "preparation";

  // Rebuilt per dialogue: the reporter caps how many crumbs it writes, so a
  // reattempt sharing one would spend the previous dialogue's budget and tag its
  // crumbs with the previous dialogue's id.
  const { tracker: levelReporter } = useSampleTracker(
    () => createAudioLevelWindowReporter(dialogueId ?? undefined),
    dialogueId ?? undefined
  );

  const handleTrackStatusChange = (info: TrackStatusInfo) => {
    const benignCameraToggle =
      info.kind === "video" && !cameraEnabledRef.current;
    if (benignCameraToggle) return;
    // "unmuted" crumb
    recordingBreadcrumb(`${info.kind} track ${info.status}`, {
      dialogue_id: dialogueId,
    });
  };

  const handleAudioRecovered = (label: string) => {
    recordingBreadcrumb("audio recovered", {
      dialogue_id: dialogueId,
      device_label: label || undefined,
    });
    const device = label ? ` (${label})` : "";
    toast.success(`Microphone reconnected${device}`);
  };

  const handleAudioRecoveryFailed = () => {
    recordingBreadcrumb("audio recovery failed", { dialogue_id: dialogueId });
    toast.error("We couldn't reconnect your microphone");
  };

  const telemetry = useWebcamCaptureTelemetry({
    surface: "socratic",
    dialogueId: dialogueId ?? undefined,
  });

  const recorder = useWebcamCapture({
    initialMode: "video",
    cameraEnabled: initialCameraEnabled,
    preferredDevices: getStoredPreferredDevices(),
    maxFileSize: 200 * 1024 * 1024,
    recordingConfig: {
      maxDurationSeconds: dialogueTimeline.responseTimeSeconds ?? 240,
      bitrate: 1000000,
    },
    // The turn starts exactly when the recorder does — the one true, one-shot
    // signal. Firing here instead of from the reactive start guard keeps it from
    // repeating when the recorder stops while redux still reads `isRecording`.
    onRecordingStarted() {
      onRecordingStart?.(getCurrentDate().toISOString());
    },
    onMediaCaptured(media, duration, diagnostics) {
      onCaptureReady?.(
        media,
        duration,
        diagnostics,
        getCurrentDate().toISOString()
      );
    },
    onTrackStatusChange: handleTrackStatusChange,
    onAudioRecovered: handleAudioRecovered,
    onAudioRecoveryFailed: handleAudioRecoveryFailed,
    onLevelReading,
    onLevelWindow(window) {
      levelReporter().report(window, phaseRef.current);
    },
    ...telemetry,
  });

  cameraEnabledRef.current = recorder.cameraEnabled;

  const {
    cameraEnabled,
    error,
    hasPermission,
    isVideoElementReady,
    isRecording,
    startCamera,
    startRecording,
    stopRecording,
  } = recorder;

  // Acquire the camera for any attempt still awaiting a recording, not just one
  // in preparation: a student resuming after a refresh starts with preparation
  // already over and `isRecording` already true, and would otherwise never be
  // asked for the camera, leaving the auto-start below waiting on a permission
  // that never arrives.
  //
  // `hasPermission` stops this repeating once the camera is up, and `error`
  // keeps it away from a capture that has already failed: `startCamera` stops
  // every live track before asking for a new stream, which would pull the
  // tracks out from under a recorder still running.
  //
  // Only require a mounted video element when the camera is on; an audio-only
  // stream has no preview surface to attach to.
  useEffect(() => {
    if (
      !dialogueTimeline.recordingComplete &&
      !hasPermission &&
      error == null &&
      (!cameraEnabled || isVideoElementReady)
    ) {
      void startCamera();
    }
  }, [
    dialogueTimeline.recordingComplete,
    hasPermission,
    error,
    isVideoElementReady,
    startCamera,
    cameraEnabled,
  ]);

  // Auto-start recording when preparation ends. startTurn is fired from the
  // recorder's onRecordingStarted, so a no-op startRecording() (e.g. stream
  // already torn down) can never re-fire the turn.
  useEffect(() => {
    if (
      dialogueTimeline.preparationEndDate.passed == true &&
      dialogueTimeline.isRecording &&
      !isRecording &&
      hasPermission &&
      error == null &&
      !blockAutoStart
    ) {
      startRecording();
    }
  }, [
    dialogueTimeline.preparationEndDate.passed,
    dialogueTimeline.isRecording,
    isRecording,
    hasPermission,
    error,
    startRecording,
    blockAutoStart,
  ]);

  // Auto-stop recording when response time expires
  useEffect(() => {
    if (dialogueTimeline.responseEndDate.passed == true && isRecording) {
      stopRecording();
      track(JitsuEvent.VIDEO_RESPONSE_TIME_ELAPSED, {
        dialogue_id: dialogueTimeline.dialogueId,
      });
    }
  }, [
    dialogueTimeline.responseEndDate.passed,
    isRecording,
    stopRecording,
    track,
    dialogueTimeline.dialogueId,
  ]);

  const handleStartRecordingEarly = () => {
    track(JitsuEvent.VIDEO_RESPONSE_PREPARATION_SKIPPED, {
      dialogue_id: dialogueTimeline.dialogueId,
    });
    dispatch(startRecordingEarly());
  };

  const handleStopRecordingEarly = () => {
    track(JitsuEvent.VIDEO_RESPONSE_REMAINING_TIME_SKIPPED, {
      dialogue_id: dialogueTimeline.dialogueId,
    });
    dispatch(stopRecordingEarly());
    if (recorder.isRecording) {
      recorder.stopRecording();
    }
  };

  const handleSubmit = () => {
    // TODO: Save/submit the video
    if (recorder.capturedMedia != null) {
      console.info("Submit recording:", recorder.capturedMedia.file);
    }
  };

  return {
    recorder,
    startRecordingEarly: handleStartRecordingEarly,
    stopRecordingEarly: handleStopRecordingEarly,
    submit: handleSubmit,
  };
}
